import React, { useState } from 'react'
import NumberFormat from 'react-number-format'
import type { InputFieldProps, InputFieldFormat } from '../InputField'

// Strip the display formatting NumberFormat adds so consumers receive a plain numeric string.
// Paired with `thousandSeparator` (comma) + a `$` prefix below: the DOM value is the formatted
// display (e.g. "1,000"), and a consumer that parseFloat()s it would read parseFloat("1,000") === 1
// - values >= 1000 collapsed to their first digit group (#20987). Both onChange and onBlur run this
// so the value they forward is identical; keep it in sync with the separators configured below.
const stripDisplayFormatting = (value: string): string => value.replaceAll(/\$|,/g, '')

// Helper for formatting input
const FormattedInput = (
  props: InputFieldProps & { format: InputFieldFormat; allowNegativeNumberInputs?: boolean; allowZero?: boolean }
) => {
  const {
    inputRef: _inputRef,
    onChange,
    onBlur,
    format,
    name,
    prefix,
    suffix,
    value: rawValue,
    allowNegativeNumberInputs,
    allowZero,
    ...other
  } = props

  const thousandSeparator = true
  const fixedDecimalScale = false // format === 'price'
  const [blurLoad, setBlurLoad] = useState<boolean>(false)
  const [selected, setSelected] = useState<boolean>(false)
  const emptyValueLoad =
    format !== 'custom' &&
    (rawValue === '0.00' || (rawValue === 0 && !blurLoad && selected && ['price', 'rate'].includes(format)))
  const value =
    ['dimension', 'hours'].includes(format) && rawValue === 0 && !allowZero
      ? ''
      : emptyValueLoad
        ? format === 'rate'
          ? undefined
          : ''
        : rawValue
  const decimalScale = format === 'coordinate' ? 6 : format === 'taxRate' ? 3 : emptyValueLoad ? 0 : 2
  const allowNegative = allowNegativeNumberInputs || ['price', 'custom', 'coordinate'].includes(format)

  return (
    <NumberFormat
      {...(other as any)}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          ...event,
          target: {
            ...event.target,
            name,
            value: stripDisplayFormatting(event.target.value)
          }
        })
      }}
      name={name}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        setSelected(false)
        setBlurLoad(true)
        // Forward the blur so consumers' onBlur handlers fire (MUI InputBase injects its handleBlur
        // here; without this it is swallowed and onBlur never reaches the consumer for formatted
        // fields - unlike non-formatted inputs where it works). Needed for round-on-blur (#20987).
        // Sanitize exactly like onChange (see stripDisplayFormatting) so blur and change forward the
        // same value; otherwise a consumer that parses the blur value truncates amounts >= 1000.
        onBlur?.({
          ...event,
          target: {
            ...event.target,
            name,
            value: stripDisplayFormatting(event.target.value)
          }
        } as React.FocusEvent<HTMLInputElement>)
      }}
      onFocus={() => {
        setSelected(true)
        setBlurLoad(false)
      }}
      thousandSeparator={thousandSeparator}
      prefix={prefix}
      suffix={suffix}
      fixedDecimalScale={fixedDecimalScale}
      decimalScale={decimalScale}
      value={value as any}
      allowNegative={allowNegative}
    />
  )
}

export default FormattedInput
